import { ChatBotRecordRole, ChatConfig, useGetApiChatListChatModelIds } from "@/API";
import { TooltipMultiline } from "@/Components/TooltipMultiline";
import { Button, Group, Text, Modal, NumberInput, Select, Switch, Textarea, TextInput, Tooltip, Box, Collapse, ActionIcon, Stack, Paper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp, IconPlus, IconTrash } from "@tabler/icons-react";
import { PropsWithChildren, useEffect } from "react";
import { string } from "zod";

export function ChatConfigEditButton(props: { config?: ChatConfig, onChange?: (config: ChatConfig) => void } & PropsWithChildren) {
    const [opened, { open, close }] = useDisclosure(false);
    const [systemPrompt_opened, { toggle: systemPrompt_toggle }] = useDisclosure(false);
    const [additionalPrompts_opened, { toggle: additionalPrompts_toggle }] = useDisclosure(false);
    const form = useForm<ChatConfig>({
        initialValues: props.config,
        validate: {
        }
    });
    const modelList = useGetApiChatListChatModelIds()
    useEffect(() => {
        if (opened) {
            if (props.config) form.setValues(props.config)
            else form.reset()
        }
    }, [opened])
    const variableHints = `變數:
    當前時間:
    {{$Now}}
    範例:2025/1/1 上午 00:00:00
    {{$Now:yyyy-MM-dd HH:mm:ss|GMT8}}
    範例:2025-01-01 00:00:00
    {{$Now:yyyy-MM-dd HH:mm:ss|GMT+8}}
    範例:2025-01-01 00:00:00
    {{$Now:yyyy-MM-dd HH:mm:ss|Asia/Taipei}}
    範例:2025-01-01 00:00:00
    {{$UtcNow:o}}
    範例:2025-01-01T00:00:000.0000000Z`

    const additionalPromptsFields = form.getValues().additionalPrompts?.map((item, index) => (
        <Paper key={index} withBorder shadow="sm" p="sm">
            <Stack key={index} gap={0}>
                <Select
                    label="角色"
                    defaultValue={ChatBotRecordRole.System}
                    withAsterisk
                    allowDeselect={false}
                    data={[ChatBotRecordRole.System, ChatBotRecordRole.User, ChatBotRecordRole.Assistant]}
                    {...form.getInputProps(`additionalPrompts.${index}.role`)}
                />
                <Textarea
                    label="提示詞"
                    placeholder="prompt"
                    withAsterisk
                    autosize
                    maxRows={10}
                    {...form.getInputProps(`additionalPrompts.${index}.message`)}
                />
                <ActionIcon color="red" onClick={() => form.removeListItem('additionalPrompts', index)}>
                    <IconTrash size={16} />
                </ActionIcon>
            </Stack>
        </Paper>
    ));

    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} title="聊天設定" size="xl" closeOnClickOutside={false}>
            <form onSubmit={form.onSubmit((values) => {
                props.onChange?.(values)
                // close()
            })}>
                <Select
                    label="Model"
                    data={modelList.data?.data}
                    {...form.getInputProps('modelId')}
                />
                <TextInput
                    label="名稱"
                    placeholder="optional"
                    {...form.getInputProps('systemName')}
                />
                <Tooltip color="gray" position="top-start" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={variableHints}>
                    <Stack gap={0} onClick={systemPrompt_toggle}>
                        <Group gap={0}>
                            <Text>
                                系統提示詞
                            </Text>
                            <ActionIcon variant="transparent">
                                {systemPrompt_opened ?
                                    <IconChevronUp stroke={2} /> : <IconChevronDown stroke={2} />
                                }
                            </ActionIcon>
                        </Group>
                        <Text size="xs" fw={300} c="gray">
                            系統提示詞，用於定義聊天機器人性格、對話規則等
                        </Text>
                    </Stack>
                </Tooltip>
                <Collapse expanded={systemPrompt_opened}>
                    <Textarea
                        autosize
                        maxRows={20}
                        {...form.getInputProps('systemPrompt')}
                    />
                </Collapse>
                <Tooltip color="gray" position="top-start" multiline events={{ hover: true, focus: false, touch: true }} style={{ whiteSpace: "pre-line" }} label={variableHints}>
                    <Stack gap={0} onClick={additionalPrompts_toggle}>
                        <Group gap={0}>
                            <Text>
                                額外提示詞
                            </Text>
                            <ActionIcon variant="transparent">
                                {additionalPrompts_opened ?
                                    <IconChevronUp stroke={2} /> : <IconChevronDown stroke={2} />
                                }
                            </ActionIcon>
                        </Group>
                        <Text size="xs" fw={300} c="gray">
                            額外提示詞，放在系統提示詞之後
                        </Text>
                    </Stack>
                </Tooltip>
                <Collapse expanded={additionalPrompts_opened}>
                    {additionalPromptsFields}
                    <ActionIcon color="green" mt="md" onClick={() =>
                        form.insertListItem('additionalPrompts', {})
                    }>
                        <IconPlus size={16} />
                    </ActionIcon>
                </Collapse>
                <NumberInput
                    label="最大歷史紀錄個數"
                    description="會將過去問答納入下次回答的依據，一問一答算一個個數"
                    min={0}
                    max={20}
                    step={1}
                    {...form.getInputProps('maxHistoryRecordsCount')}
                />
                <NumberInput
                    label="歷史紀錄過期時間(秒)"
                    description="超過此時間的歷史紀錄不會被加入上下文，設定0為禁用此功能"
                    min={0}
                    step={1}
                    {...form.getInputProps('historyExpiredTime')}
                />
                <NumberInput
                    label="最大搜尋資料Tokens"
                    description="搜尋結果會依照相似度加入Context，直到Token總數超過此值"
                    min={0}
                    step={1}
                    {...form.getInputProps('maxSearchContextTokens')}
                />
                <NumberInput
                    label="語意搜尋相似度閾值"
                    description="語意搜尋相似度超過該值才會被加入結果"
                    min={0}
                    max={1}
                    step={0.1}
                    {...form.getInputProps('embeddingThreshold')}
                />
                <Switch
                    my="md"
                    label="是否允許同時呼叫多個工具(部分模型不支援)"
                    {...form.getInputProps('allowParallelCalls', { type: 'checkbox' })}
                />
                <Switch
                    my="md"
                    label="是否在回應的歷史紀錄，保留工具使用紀錄"
                    {...form.getInputProps('keepToolRecords', { type: 'checkbox' })}
                />
                <Group>
                    <NumberInput
                        label="Temperature"
                        description="控制隨機性的參數"
                        step={0.1}
                        {...form.getInputProps('temperature')}
                    />
                    <NumberInput
                        label="TopP"
                        description="控制模型在生成下一個詞時，只考慮累積機率達到 p 的候選詞"
                        max={1}
                        step={0.1}
                        {...form.getInputProps('topP')}
                    />
                    <NumberInput
                        label="Seed"
                        description="隨機種子，設定0為不使用"
                        allowDecimal={false}
                        {...form.getInputProps('seed')}
                        onChange={(val) => form.setFieldValue('seed', val === '' || val === null || val === undefined || isNaN(Number(val)) ? undefined : Number(val))}
                    />
                </Group>
                <Group mt="md">
                    <NumberInput
                        label="DenseWeight"
                        description="語義搜尋權重"
                        step={0.1}
                        min={0}
                        {...form.getInputProps('denseWeight')}
                    />
                    <NumberInput
                        label="SparseWeight"
                        description="關鍵字搜尋權重"
                        step={0.1}
                        min={0}
                        {...form.getInputProps('sparseWeight')}
                    />
                </Group>
                <Group justify="flex-end" mt="md">
                    <Button type="submit">保存</Button>
                </Group>
            </form>
        </Modal>
    </>
}